import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { doc, onSnapshot, type Timestamp } from "firebase/firestore";
import { notifications } from "@mantine/notifications";
import { auth, db, isFirebaseConfigured } from "./firebase";
import {
  AuthContext,
  type AuthContextValue,
  type UserProfile,
} from "./authContext";
import {
  changeUsername as changeUsernameApi,
  claimUsername as claimUsernameApi,
} from "./usernames";
import { updateProfile as updateProfileApi, type ProfileEdit } from "./profile";

const EMAIL_STORAGE_KEY = "nifusion:email-signin";

type UserDocRaw = {
  username?: string;
  usernameLower?: string;
  usernameChangedAt?: Timestamp | null;
  displayName?: string | null;
  photoURL?: string | null;
  email?: string | null;
  bio?: string | null;
  accentColor?: string | null;
  createdAt?: Timestamp | null;
};

function toProfile(data: UserDocRaw): UserProfile | null {
  if (!data.username || !data.usernameLower) return null;
  return {
    username: data.username,
    usernameLower: data.usernameLower,
    usernameChangedAt: data.usernameChangedAt ?? null,
    displayName: data.displayName ?? null,
    photoURL: data.photoURL ?? null,
    email: data.email ?? null,
    bio: data.bio ?? null,
    accentColor: data.accentColor ?? null,
    createdAt: data.createdAt ?? null,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(() => Boolean(auth));
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileState, setProfileState] = useState<
    "idle" | "loading" | "missing" | "ready"
  >("idle");
  const completingLinkRef = useRef(false);

  useEffect(() => {
    if (!auth) return;
    return onAuthStateChanged(auth, (next) => {
      setUser(next);
      setLoading(false);
      if (!next) {
        setProfile(null);
        setProfileState("idle");
      }
    });
  }, []);

  useEffect(() => {
    if (!db) return;
    if (!user) return;
    setProfileState("loading");
    const ref = doc(db, "users", user.uid);
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setProfile(null);
          setProfileState("missing");
          return;
        }
        const next = toProfile(snap.data() as UserDocRaw);
        setProfile(next);
        setProfileState(next ? "ready" : "missing");
      },
      () => {
        setProfileState("missing");
      }
    );
  }, [user]);

  useEffect(() => {
    if (!auth) return;
    if (typeof window === "undefined") return;
    if (completingLinkRef.current) return;
    const href = window.location.href;
    if (!isSignInWithEmailLink(auth, href)) return;
    completingLinkRef.current = true;

    let storedEmail = window.localStorage.getItem(EMAIL_STORAGE_KEY);
    if (!storedEmail) {
      storedEmail =
        window.prompt(
          "Confirm the email address you used to request this sign-in link"
        ) ?? "";
    }
    if (!storedEmail) return;

    signInWithEmailLink(auth, storedEmail, href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_STORAGE_KEY);
        const url = new URL(href);
        for (const key of [
          "apiKey",
          "oobCode",
          "mode",
          "lang",
          "continueUrl",
        ]) {
          url.searchParams.delete(key);
        }
        window.history.replaceState({}, "", url.toString());
        notifications.show({
          color: "green",
          title: "Signed in",
          message: "Welcome back.",
        });
      })
      .catch(() => {
        notifications.show({
          color: "red",
          title: "Sign-in link failed",
          message:
            "The link is expired or invalid. Request a fresh one and try again.",
        });
      });
  }, []);

  const needsUsername = Boolean(user) && profileState === "missing";
  const username = profile?.username ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: isFirebaseConfigured,
      profile,
      username,
      needsUsername,
      signInWithGoogle: async () => {
        if (!auth) return;
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
      },
      signOutUser: async () => {
        if (!auth) return;
        await signOut(auth);
      },
      sendEmailSignInLink: async (email: string) => {
        if (!auth) return;
        const actionCodeSettings = {
          url: window.location.origin + "/",
          handleCodeInApp: true,
        };
        await sendSignInLinkToEmail(auth, email, actionCodeSettings);
        window.localStorage.setItem(EMAIL_STORAGE_KEY, email);
      },
      claimUsername: async (name: string) => {
        if (!user) throw new Error("Sign in first.");
        await claimUsernameApi(user.uid, name, {
          displayName: user.displayName,
          photoURL: user.photoURL,
          email: user.email,
        });
      },
      changeUsername: async (newName: string) => {
        if (!user) throw new Error("Sign in first.");
        if (!profile) throw new Error("Profile is still loading.");
        await changeUsernameApi(
          user.uid,
          profile.usernameLower,
          newName,
          profile.usernameChangedAt
        );
      },
      updateProfile: async (edit: ProfileEdit) => {
        if (!user) throw new Error("Sign in first.");
        await updateProfileApi(user.uid, edit);
      },
    }),
    [user, loading, profile, username, needsUsername]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
